import ApiService from '../api'

class GalleryApiService {
  normalizeGalleryData(gallery) {
    if (!gallery) return null

    const itemsCount = gallery.items_count ??
      (gallery.items ? gallery.items.length : 0) ??
      0

    // FIXED: Handle is_public as integer (0/1) from database
    const isPublic = gallery.is_public === true ||
      gallery.is_public === 1 ||
      gallery.is_public === '1'

    return {
      ...gallery,
      items_count: itemsCount,
      items: gallery.items || [],
      is_public: isPublic, // Now properly converted to boolean
      created_at: gallery.created_at || null,
      updated_at: gallery.updated_at || null
    }
  }

  // Helper method to handle different API response structures
  extractResponseData(response) {
    // Handle different response wrapper structures
    if (response && response.Data && response.Data.Data) {
      const data = response.Data.Data

      // Handle nested success structure
      if (data && data.status === 'success' && data.data) {
        return data.data
      }

      // Handle direct data structure
      if (data && !data.status) {
        return data
      }
    }

    // Handle Success/Data structure
    if (response && response.Success && response.Data) {
      const data = response.Data

      if (data && data.status === 'success' && data.data) {
        return data.data
      }

      if (data && !data.status) {
        return data
      }
    }

    // Handle direct data response
    if (response && response.data) {
      return response.data
    }

    console.warn('Could not extract data from response structure:', response)
    return null
  }

  // Get public galleries with pagination (for public gallery page)
  async getPublicGalleries(params = {}) {
    try {
      const searchParams = {
        include_private: 'false', // Only public galleries
        per_page: 12,
        ...params
      }

      const response = await ApiService.get('/galleries', searchParams)

      const data = this.extractResponseData(response)

      if (data) {
        // Handle paginated response
        if (data.data && Array.isArray(data.data)) {
          return {
            data: data.data.map(gallery => this.normalizeGalleryData(gallery)),
            pagination: {
              current_page: data.current_page || 1,
              last_page: data.last_page || 1,
              per_page: data.per_page || 12,
              total: data.total || 0
            }
          }
        }
        // Handle direct array response
        else if (Array.isArray(data)) {
          return {
            data: data.map(gallery => this.normalizeGalleryData(gallery)),
            pagination: null
          }
        }
      }

      console.warn('No valid data found in response')
      return {
        data: [],
        pagination: null
      }
    } catch (error) {
      console.error('Error fetching public galleries:', error)

      // Check if it's a network error
      if (!navigator.onLine) {
        throw new Error('No internet connection. Please check your network.')
      }

      // Check for specific error types
      if (error.response) {
        const status = error.response.status
        if (status === 404) {
          throw new Error('Gallery service not found. Please contact support.')
        } else if (status === 403) {
          throw new Error('Access denied. You may not have permission to view galleries.')
        } else if (status === 500) {
          throw new Error('Server error. Please try again later.')
        } else if (status >= 400) {
          throw new Error(`Request failed (${status}). Please try again.`)
        }
      }

      throw new Error(this.getErrorMessage(error))
    }
  }

  // Get all galleries with better error handling and data normalization
  async getGalleries(params = {}) {
    try {
      const searchParams = {
        include_private: 'true',
        per_page: 50,
        ...params
      }

      const response = await ApiService.get('/galleries', searchParams)

      const data = this.extractResponseData(response)

      if (data) {
        // Handle paginated response
        if (data.data && Array.isArray(data.data)) {
          return data.data.map(gallery => this.normalizeGalleryData(gallery))
        }
        // Handle direct array response
        else if (Array.isArray(data)) {
          return data.map(gallery => this.normalizeGalleryData(gallery))
        }
      }

      console.warn('No valid data found in galleries response')
      return []
    } catch (error) {
      console.error('Error fetching galleries:', error)
      throw new Error(this.getErrorMessage(error))
    }
  }

  // Get single gallery
  async getGallery(galleryId) {
    try {
      const response = await ApiService.get(`/galleries/${galleryId}`)

      const data = this.extractResponseData(response)

      if (data) {
        return this.normalizeGalleryData(data)
      }

      throw new Error('Gallery not found or invalid response format')
    } catch (error) {
      console.error(`Error fetching gallery ${galleryId}:`, error)

      if (error.response?.status === 404) {
        throw new Error('Gallery not found')
      }

      throw new Error(this.getErrorMessage(error))
    }
  }

  // Create new gallery with better validation and error handling
  async createGallery(galleryData) {
    try {
      const validation = this.validateGalleryData(galleryData)
      if (!validation.isValid) {
        throw new Error(`Validation failed: ${validation.errors.join(', ')}`)
      }

      // Prepare data for API
      const requestData = {
        title: galleryData.title.trim(),
        description: galleryData.description || '',
        is_public: Boolean(galleryData.is_public)
      }

      let payload = requestData

      if (galleryData.cover_image) {
        const formData = new FormData()
        formData.append('title', requestData.title)
        formData.append('description', requestData.description)
        formData.append('is_public', requestData.is_public ? '1' : '0')
        formData.append('cover_image', galleryData.cover_image)
        payload = formData
      }

      const response = await ApiService.post('/galleries', {}, payload)

      const data = this.extractResponseData(response)

      if (data) {
        return this.normalizeGalleryData(data)
      }

      throw new Error(response?.Message || 'Failed to create gallery')
    } catch (error) {
      console.error('Error creating gallery:', error)
      throw new Error(this.getErrorMessage(error))
    }
  }

  // Update gallery
  async updateGallery(galleryId, galleryData) {
    try {
      const validation = this.validateGalleryData(galleryData)
      if (!validation.isValid) {
        throw new Error(`Validation failed: ${validation.errors.join(', ')}`)
      }

      const requestData = {
        title: galleryData.title.trim(),
        description: galleryData.description || '',
        is_public: Boolean(galleryData.is_public),
        remove_cover_image: galleryData.remove_cover_image ? '1' : '0'
      }

      let response

      if (galleryData.cover_image) {
        // Use POST with _method=PUT (method spoofing) so FormData reaches the update controller
        const formData = new FormData()
        formData.append('_method', 'PUT')
        formData.append('title', requestData.title)
        formData.append('description', requestData.description)
        formData.append('is_public', requestData.is_public ? '1' : '0')
        formData.append('remove_cover_image', '0')
        formData.append('cover_image', galleryData.cover_image)
        response = await ApiService.post(`/galleries/${galleryId}`, {}, formData)
      } else {
        response = await ApiService.put(`/galleries/${galleryId}`, {}, requestData)
      }

      const data = this.extractResponseData(response)

      if (data) {
        return this.normalizeGalleryData(data)
      }

      throw new Error(response?.Message || 'Failed to update gallery')
    } catch (error) {
      console.error(`Error updating gallery ${galleryId}:`, error)
      throw new Error(this.getErrorMessage(error))
    }
  }

  // Delete gallery
  async deleteGallery(galleryId) {
    try {
      const response = await ApiService.delete(`/galleries/${galleryId}`)

      if (response && response.Success) {
        return response.Data || { success: true }
      }

      throw new Error(response?.Message || 'Failed to delete gallery')
    } catch (error) {
      console.error(`Error deleting gallery ${galleryId}:`, error)
      throw new Error(this.getErrorMessage(error))
    }
  }

  // Every item of a gallery. The API pages an album's items (20 per page unless
  // asked), so an album with more than that needs several requests.
  async getAllGalleryItems(galleryId, pageSize = 100) {
    const items = []
    let total = null
    let page = 1

    // The page cap is only a guard so a misbehaving response can never loop forever.
    for (let guard = 0; guard < 500; guard++) {
      const result = await this.getGalleryItems(galleryId, { per_page: pageSize, page })
      const pageItems = result.data || []
      items.push(...pageItems)

      const pagination = result.pagination
      if (pagination && pagination.total != null) total = pagination.total

      if (!pagination || pageItems.length === 0 || page >= pagination.last_page) break
      page++
    }

    return { data: items, total: total ?? items.length }
  }

  // Get gallery items - FIXED VERSION
  async getGalleryItems(galleryId, params = {}) {
    try {
      const response = await ApiService.get(`/galleries/${galleryId}/items`, params)

      const data = this.extractResponseData(response)

      if (data) {
        // Handle paginated response structure
        if (data.data && Array.isArray(data.data)) {
          return {
            data: data.data,
            pagination: {
              current_page: data.current_page || 1,
              last_page: data.last_page || 1,
              per_page: data.per_page || 20,
              total: data.total || 0
            }
          }
        }
        // Handle direct array response
        else if (Array.isArray(data)) {
          return { data: data, pagination: null }
        }
        // Handle case where data is empty but valid
        else if (data && typeof data === 'object' && !Array.isArray(data)) {
          return { data: [], pagination: null }
        }
      }

      console.warn(`No valid items data found for gallery ${galleryId}`)
      return { data: [], pagination: null }
    } catch (error) {
      console.error(`Error fetching gallery ${galleryId} items:`, error)

      // Don't throw error if it's just empty - return empty array
      if (error.response?.status === 404) {
        return { data: [], pagination: null }
      }

      throw new Error(this.getErrorMessage(error))
    }
  }

  // Add single item to gallery with improved error handling
  async addGalleryItem(galleryId, itemData, options = {}) {
    try {
      const validation = this.validateItemData(itemData)
      if (!validation.isValid) {
        throw new Error(`Validation failed: ${validation.errors.join(', ')}`)
      }

      let requestData = itemData

      // Create FormData if we have file data
      if (itemData.file || itemData.cover_image) {
        const formData = new FormData()

        Object.keys(itemData).forEach(key => {
          if (itemData[key] !== null && itemData[key] !== undefined && itemData[key] !== '') {
            formData.append(key, itemData[key])
          }
        })

        requestData = formData
      }

      const endpoint = `/galleries/${galleryId}/items`
      const response = options.onProgress && requestData instanceof FormData
        ? await ApiService.postFormWithProgress(endpoint, requestData, options.onProgress)
        : await ApiService.post(endpoint, {}, requestData)

      const data = this.extractResponseData(response)

      if (data) {
        return data
      }

      throw new Error(response?.Message || 'Failed to add item to gallery')
    } catch (error) {
      console.error(`Error adding item to gallery ${galleryId}:`, error)
      throw new Error(this.getErrorMessage(error))
    }
  }

  // Bulk upload items to gallery with progress tracking
  async bulkUploadItems(galleryId, files, options = {}) {
    try {
      if (!files || files.length === 0) {
        throw new Error('No files provided for upload')
      }

      if (files.length > 20) {
        throw new Error('Maximum 20 files allowed per upload')
      }

      // Validate all files first
      const validationResults = files.map(file => this.validateFileData(file))
      const invalidFiles = validationResults.filter(result => !result.isValid)

      if (invalidFiles.length > 0) {
        throw new Error(`Invalid files: ${invalidFiles.map(r => r.errors.join(', ')).join('; ')}`)
      }

      const formData = new FormData()

      // Add files - use files[] for Laravel array handling
      files.forEach((file, index) => {
        formData.append('files[]', file)
      })

      // Add options
      if (options.default_title_prefix) {
        formData.append('default_title_prefix', options.default_title_prefix)
      }

      // Index-aligned with files[] - the backend falls back to a
      // filename-derived title for any slot left blank.
      if (Array.isArray(options.titles)) {
        options.titles.forEach((title) => {
          formData.append('titles[]', title || '')
        })
      }

      // Use ApiService.post which now handles FormData correctly
      const response = await ApiService.post(`/galleries/${galleryId}/bulk-upload`, {}, formData)

      // Handle the response structure more robustly
      let responseData = null

      // Try different response structures
      if (response?.Data?.Data) {
        responseData = response.Data.Data
      } else if (response?.Data) {
        responseData = response.Data
      } else if (response?.data) {
        responseData = response.data
      } else {
        responseData = response
      }

      // Check if it's a successful response
      if (responseData && (responseData.status === 'success' || responseData.status === 'partial')) {
        return {
          status: responseData.status,
          message: responseData.message || 'Files uploaded successfully',
          uploaded_items: responseData.uploaded_items || [],
          errors: responseData.errors || []
        }
      }

      // Check for error responses
      if (responseData && responseData.status === 'error') {
        throw new Error(responseData.message || 'Upload failed')
      }

      // Handle validation errors
      if (responseData && responseData.errors) {
        const errorMessages = typeof responseData.errors === 'object'
          ? Object.values(responseData.errors).flat()
          : [responseData.errors]
        throw new Error(`Validation failed: ${errorMessages.join(', ')}`)
      }

      // If we get here, something unexpected happened
      console.error('Unexpected response structure:', response)
      throw new Error('Unexpected response format from server')

    } catch (error) {
      console.error(`Error bulk uploading to gallery ${galleryId}:`, error)

      // Enhanced error handling
      if (error.response) {
        console.error('Error response:', error.response)
        const status = error.response.status
        const data = error.response.data

        if (status === 413) {
          throw new Error('Files too large. Maximum 50MB per file.')
        } else if (status === 422) {
          // Validation errors
          if (data?.Data?.errors) {
            const errorMessages = Object.values(data.Data.errors).flat()
            throw new Error(`Validation failed: ${errorMessages.join(', ')}`)
          }
          throw new Error('Validation failed. Please check your files.')
        } else if (status >= 500) {
          throw new Error('Server error. Please try again later.')
        }
      }

      throw new Error(this.getErrorMessage(error))
    }
  }

  // Add YouTube video to gallery
  async addYouTubeVideo(galleryId, videoData) {
    try {
      const validation = this.validateYouTubeData(videoData)
      if (!validation.isValid) {
        throw new Error(`Validation failed: ${validation.errors.join(', ')}`)
      }

      const requestData = {
        title: videoData.title.trim(),
        description: videoData.description || '',
        type: 'video',
        youtube_url: videoData.youtube_url.trim(),
        sort_order: videoData.sort_order || 0
      }

      const response = await ApiService.post(`/galleries/${galleryId}/items`, {}, requestData)

      const data = this.extractResponseData(response)

      if (data) {
        return data
      }

      throw new Error(response?.Message || 'Failed to add YouTube video')
    } catch (error) {
      console.error(`Error adding YouTube video to gallery ${galleryId}:`, error)
      throw new Error(this.getErrorMessage(error))
    }
  }

  // Add Vimeo video to gallery
  async addVimeoVideo(galleryId, videoData) {
    try {
      const validation = this.validateVimeoData(videoData)
      if (!validation.isValid) {
        throw new Error(`Validation failed: ${validation.errors.join(', ')}`)
      }

      const requestData = {
        title: videoData.title.trim(),
        description: videoData.description || '',
        type: 'video',
        vimeo_url: videoData.vimeo_url.trim(),
        sort_order: videoData.sort_order || 0
      }

      const response = await ApiService.post(`/galleries/${galleryId}/items`, {}, requestData)

      const data = this.extractResponseData(response)
      if (data) return data

      throw new Error(response?.Message || 'Failed to add Vimeo video')
    } catch (error) {
      console.error(`Error adding Vimeo video to gallery ${galleryId}:`, error)
      throw new Error(this.getErrorMessage(error))
    }
  }

  // Validate YouTube video data
  validateYouTubeData(videoData) {
    const errors = []

    if (!videoData || typeof videoData !== 'object') {
      errors.push('Video data is required')
      return { isValid: false, errors }
    }

    if (!videoData.title || !videoData.title.trim()) {
      errors.push('Video title is required')
    } else if (videoData.title.trim().length > 255) {
      errors.push('Video title must be less than 255 characters')
    }

    if (!videoData.youtube_url || !videoData.youtube_url.trim()) {
      errors.push('YouTube URL is required')
    } else if (!this.isValidYouTubeUrl(videoData.youtube_url)) {
      errors.push('Invalid YouTube URL format')
    }

    if (videoData.description && videoData.description.length > 1000) {
      errors.push('Video description must be less than 1000 characters')
    }

    return {
      isValid: errors.length === 0,
      errors
    }
  }

  validateVimeoData(videoData) {
    const errors = []

    if (!videoData || typeof videoData !== 'object') {
      errors.push('Video data is required')
      return { isValid: false, errors }
    }

    if (!videoData.title || !videoData.title.trim()) {
      errors.push('Video title is required')
    } else if (videoData.title.trim().length > 255) {
      errors.push('Video title must be less than 255 characters')
    }

    if (!videoData.vimeo_url || !videoData.vimeo_url.trim()) {
      errors.push('Vimeo URL is required')
    } else if (!this.isValidVimeoUrl(videoData.vimeo_url)) {
      errors.push('Invalid Vimeo URL format')
    }

    if (videoData.description && videoData.description.length > 1000) {
      errors.push('Video description must be less than 1000 characters')
    }

    return {
      isValid: errors.length === 0,
      errors
    }
  }

  // Remove item from gallery
  async removeGalleryItem(galleryId, itemId) {
    try {
      const response = await ApiService.delete(`/galleries/${galleryId}/items/${itemId}`)

      if (response && response.Success) {
        return response.Data || { success: true }
      }

      throw new Error(response?.Message || 'Failed to remove item')
    } catch (error) {
      console.error(`Error removing item ${itemId} from gallery ${galleryId}:`, error)
      throw new Error(this.getErrorMessage(error))
    }
  }

  // Set featured item
  async setFeaturedItem(galleryId, itemId) {
    try {
      const response = await ApiService.post(`/galleries/${galleryId}/items/${itemId}/feature`)

      if (response && response.Success) {
        return response.Data || { success: true }
      }

      throw new Error(response?.Message || 'Failed to set featured item')
    } catch (error) {
      console.error(`Error setting featured item ${itemId} in gallery ${galleryId}:`, error)
      throw new Error(this.getErrorMessage(error))
    }
  }

  // Helper method to extract meaningful error messages
  getErrorMessage(error) {
    if (error.response && error.response.data) {
      const data = error.response.data
      if (data.message) return data.message
      if (data.error) return data.error
      if (data.errors && typeof data.errors === 'object') {
        return Object.values(data.errors).flat().join(', ')
      }
    }
    return error.message || 'An unexpected error occurred'
  }

  // Validation methods
  validateGalleryData(galleryData) {
    const errors = []

    if (!galleryData || typeof galleryData !== 'object') {
      errors.push('Gallery data is required')
      return { isValid: false, errors }
    }

    if (!galleryData.title || !galleryData.title.trim()) {
      errors.push('Gallery title is required')
    } else if (galleryData.title.trim().length > 255) {
      errors.push('Gallery title must be less than 255 characters')
    }

    if (galleryData.description && galleryData.description.length > 1000) {
      errors.push('Gallery description must be less than 1000 characters')
    }

    if (typeof galleryData.is_public !== 'undefined' && typeof galleryData.is_public !== 'boolean') {
      errors.push('Gallery visibility must be a boolean value')
    }

    return {
      isValid: errors.length === 0,
      errors
    }
  }

  validateItemData(itemData) {
    const errors = []

    if (!itemData || typeof itemData !== 'object') {
      errors.push('Item data is required')
      return { isValid: false, errors }
    }

    // Skip validation for FormData objects (file uploads)
    if (itemData instanceof FormData) {
      return { isValid: true, errors: [] }
    }

    if (!itemData.title || !itemData.title.trim()) {
      errors.push('Item title is required')
    } else if (itemData.title.trim().length > 255) {
      errors.push('Item title must be less than 255 characters')
    }

    // Only validate type if it's provided
    if (itemData.type && !['image', 'video'].includes(itemData.type)) {
      errors.push('Item type must be either "image" or "video"')
    }

    // Check that we have either a file or YouTube URL
    // Must have file OR at least one video URL
    if (!itemData.file && !itemData.youtube_url && !itemData.vimeo_url) {
      errors.push('Either a file, YouTube URL, or Vimeo URL is required')
    }

    // Validate YouTube
    if (itemData.youtube_url && !this.isValidYouTubeUrl(itemData.youtube_url)) {
      errors.push('Invalid YouTube URL format')
    }

    // Validate Vimeo
    if (itemData.vimeo_url && !this.isValidVimeoUrl(itemData.vimeo_url)) {
      errors.push('Invalid Vimeo URL format')
    }

    if (itemData.description && itemData.description.length > 1000) {
      errors.push('Item description must be less than 1000 characters')
    }

    return {
      isValid: errors.length === 0,
      errors
    }
  }

  validateFileData(file) {
    const errors = []
    const maxSize = 50 * 1024 * 1024 // 50MB
    const allowedTypes = [
      'image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp',
      'video/mp4', 'video/quicktime', 'video/x-msvideo', 'video/x-ms-wmv'
    ]

    if (!file) {
      errors.push('File is required')
      return { isValid: false, errors }
    }

    if (!(file instanceof File)) {
      errors.push('Invalid file object')
      return { isValid: false, errors }
    }

    if (file.size > maxSize) {
      errors.push(`File size must be less than 50MB. Current size: ${this.formatFileSize(file.size)}`)
    }

    if (!allowedTypes.includes(file.type)) {
      errors.push(`Unsupported file type: ${file.type}. Allowed types: images (JPEG, PNG, GIF, WebP) and videos (MP4, MOV, AVI, WMV)`)
    }

    return {
      isValid: errors.length === 0,
      errors
    }
  }

  // Utility methods
  isValidYouTubeUrl(url) {
    const youtubeRegex = /^(https?:\/\/)?(www\.)?(youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)[a-zA-Z0-9_-]{11}/
    return youtubeRegex.test(url)
  }

  isValidVimeoUrl(url) {
    const vimeoRegex = /^(https?:\/\/)?(www\.)?vimeo\.com\/(?:video\/|channels\/[\w-]+\/)?(\d+)/;
    return vimeoRegex.test(url)
  }


  formatFileSize(bytes) {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  generateTitleFromFilename(filename) {
    return filename
      .replace(/\.[^/.]+$/, '') // Remove extension
      .replace(/[-_]/g, ' ') // Replace dashes and underscores with spaces
      .replace(/\b\w/g, l => l.toUpperCase()) // Capitalize first letters
      .trim()
  }

  formatDate(dateString, options = {}) {
    if (!dateString) return 'N/A'
    try {
      const date = new Date(dateString)
      if (isNaN(date.getTime())) return 'Invalid Date'

      const defaultOptions = {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        ...options
      }
      return date.toLocaleDateString('en-US', defaultOptions)
    } catch {
      return 'Invalid Date'
    }
  }

  debounce(func, wait = 300) {
    let timeout
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout)
        func(...args)
      }
      clearTimeout(timeout)
      timeout = setTimeout(later, wait)
    }
  }

  // Helper to get empty structures
  getEmptyGalleryStructure() {
    return {
      title: '',
      description: '',
      is_public: true,
      cover_image: null,
      items: []
    }
  }

  getEmptyItemStructure() {
    return {
      title: '',
      description: '',
      type: 'image',
      file: null,
      youtube_url: '',
      sort_order: 0,
      is_featured: false
    }
  }
}

// Export singleton instance
export default new GalleryApiService()
